import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  MessageCircle,
  Send,
  ArrowLeft,
  BadgeCheck,
  Loader2,
  Search,
  AlertCircle,
  AlertTriangle,
  Inbox,
  Plus,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';

const MessagesPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [verifiedCreators, setVerifiedCreators] = useState([]);
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [userNearBottom, setUserNearBottom] = useState(true);
  const [messageError, setMessageError] = useState(null);
  
  // Check for recipient param (when coming from "Message" button)
  const recipientId = searchParams.get('to');

  useEffect(() => {
    if (user) {
      fetchConversations();
      
      // If there's a recipient ID, start conversation with them
      if (recipientId) {
        startConversationWith(recipientId);
      }
      
      // Update activity every 30 seconds while on messages page
      const updateActivity = async () => {
        try {
          await apiClient.post('/messages/update-activity');
        } catch (err) {
          console.error('Failed to update activity:', err);
        }
      };
      
      // Update immediately
      updateActivity();
      
      // Then update every 30 seconds
      const activityInterval = setInterval(updateActivity, 30000);
      
      return () => clearInterval(activityInterval);
    }
  }, [user, recipientId]);

  useEffect(() => {
    // Scroll to bottom ONLY inside chat container, NOT the page
    // Only auto-scroll if user is already near the bottom
    if (userNearBottom && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, userNearBottom]);

  // Track if user is near bottom of chat
  const handleChatScroll = (e) => {
    const container = e.target;
    const threshold = 100; // pixels from bottom
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    setUserNearBottom(nearBottom);
  };

  // Force scroll to bottom (for new messages sent by user)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await apiClient.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerifiedCreators = async () => {
    setLoadingCreators(true);
    try {
      const response = await apiClient.get('/profile/verified-creators', {
        params: { search: creatorSearchQuery }
      });
      setVerifiedCreators(response.data);
    } catch (error) {
      console.error('Failed to fetch verified creators:', error);
      setVerifiedCreators([]);
    } finally {
      setLoadingCreators(false);
    }
  };

  useEffect(() => {
    if (showNewMessageModal) {
      fetchVerifiedCreators();
    }
  }, [showNewMessageModal, creatorSearchQuery]);

  const startConversationWith = async (userId) => {
    setMessageError(null);
    try {
      const response = await apiClient.get(`/messages/conversation-with/${userId}`);
      setOtherUser(response.data.other_user);
      setSelectedConversation(response.data.conversation_id);
      
      if (response.data.exists) {
        // Load existing conversation
        loadConversation(response.data.conversation_id);
      } else {
        // New conversation - clear messages
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error.response?.status === 403) {
        const detail = error.response.data.detail || 'Cannot start conversation';
        // Check for blocked user message
        if (detail.toLowerCase().includes('blocked') || detail.toLowerCase().includes('suspended')) {
          setMessageError('You cannot message this user because their account is blocked or suspended.');
        } else {
          setMessageError(detail);
        }
      } else {
        setMessageError('Failed to start conversation. Please try again.');
      }
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await apiClient.get(`/messages/conversations/${conversationId}`);
      setMessages(response.data.messages);
      setOtherUser({
        id: response.data.other_user_id,
        name: response.data.other_user_name,
        avatar: response.data.other_user_avatar,
        verified: response.data.other_user_verified,
        is_online: false,
        last_seen: null
      });
      setSelectedConversation(conversationId);
      
      // Reset scroll state and scroll inside container only (not the page)
      setUserNearBottom(true);
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      });
      
      // Fetch online status
      try {
        const statusRes = await apiClient.get(`/messages/user-status/${response.data.other_user_id}`);
        setOtherUser(prev => ({
          ...prev,
          is_online: statusRes.data.is_online,
          last_seen: statusRes.data.last_seen
        }));
      } catch (err) {
        console.error('Failed to fetch user status:', err);
      }
      
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !otherUser) return;

    setMessageError(null);
    setSending(true);
    try {
      const response = await apiClient.post('/messages/send', {
        recipient_id: otherUser.id,
        content: newMessage.trim(),
      });
      
      // Add message to list
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Force scroll to bottom after sending
      setUserNearBottom(true);
      setTimeout(scrollToBottom, 100);
      
      // Update conversation in sidebar
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      const detail = error.response?.data?.detail || 'Failed to send message';
      // Check for blocked user message
      if (detail.toLowerCase().includes('blocked') || detail.toLowerCase().includes('suspended')) {
        setMessageError('You cannot message this user because their account is blocked or suspended.');
      } else if (detail.toLowerCase().includes('cannot message')) {
        setMessageError(detail);
      } else {
        setMessageError('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    setOtherUser({
      id: conv.other_user_id,
      name: conv.other_user_name,
      avatar: conv.other_user_avatar,
      verified: conv.other_user_verified,
    });
    loadConversation(conv.id);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(conv =>
    conv.other_user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if user is verified
  const isVerified = user?.verified;

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Sign in Required</h2>
              <p className="text-muted-foreground mb-4">
                Please sign in to access your messages.
              </p>
              <Button onClick={() => navigate('/login')}>Sign In</Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <h2 className="text-xl font-semibold mb-2">Verification Required</h2>
              <p className="text-muted-foreground mb-4">
                You must be a verified creator to use the messaging feature.
                Complete your verification to connect with other creators.
              </p>
              <Button onClick={() => navigate('/verification')}>
                Complete Verification
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <MessageCircle className="w-8 h-8 text-emerald-500" />
              <div>
                <h1 className="text-2xl font-bold text-foreground">Messages</h1>
                <p className="text-muted-foreground">Connect with other verified creators</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowNewMessageModal(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Message
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)', minHeight: '500px' }}>
            {/* Conversations Sidebar */}
            <Card className="lg:col-span-1 flex flex-col overflow-hidden">
              <CardHeader className="pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full">
                  {loading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-center px-4">
                      <Inbox className="w-10 h-10 text-muted-foreground mb-2" />
                      <p className="text-muted-foreground text-sm">
                        {searchQuery ? 'No conversations found' : 'No messages yet'}
                      </p>
                      <p className="text-muted-foreground text-xs mt-1">
                        Start a conversation by messaging a verified creator
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredConversations.map((conv) => (
                        <button
                          key={conv.id}
                          onClick={() => selectConversation(conv)}
                          className={`w-full p-4 text-left hover:bg-accent transition-colors ${
                            selectedConversation === conv.id ? 'bg-accent' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Avatar className="w-10 h-10 relative">
                              <AvatarImage src={conv.other_user_avatar} />
                              <AvatarFallback>
                                {conv.other_user_name.charAt(0)}
                              </AvatarFallback>
                              {/* Online status indicator */}
                              {conv.other_user_online && (
                                <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-card"></span>
                              )}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-foreground truncate">
                                  {conv.other_user_name}
                                </span>
                                {conv.other_user_verified && (
                                  <BadgeCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">
                                {conv.last_message}
                              </p>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-muted-foreground">
                                  {formatTime(conv.last_message_time)}
                                </span>
                                {conv.unread_count > 0 && (
                                  <Badge className="bg-emerald-500 text-foreground text-xs px-2 py-0">
                                    {conv.unread_count}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="lg:col-span-2 flex flex-col overflow-hidden">
              {selectedConversation && otherUser ? (
                <div className="flex flex-col h-full">
                  {/* Chat Header */}
                  <CardHeader className="border-b border-border pb-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={() => {
                          setSelectedConversation(null);
                          setOtherUser(null);
                        }}
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </Button>
                      <Avatar className="w-10 h-10 relative">
                        <AvatarImage src={otherUser.avatar} />
                        <AvatarFallback>{otherUser.name?.charAt(0)}</AvatarFallback>
                        {/* Online status indicator */}
                        {otherUser.is_online && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-background"></span>
                        )}
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">
                            {otherUser.name}
                          </span>
                          {otherUser.verified && (
                            <BadgeCheck className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {otherUser.is_online ? (
                            <span className="text-emerald-500 font-medium">Online</span>
                          ) : (
                            <span>
                              {otherUser.last_seen 
                                ? `Last seen ${formatTime(otherUser.last_seen)}` 
                                : 'Verified Creator'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages - Scrollable Container with Fixed Height */}
                  <div 
                    ref={messagesContainerRef}
                    onScroll={handleChatScroll}
                    className="flex-1 overflow-y-auto p-4"
                    style={{ maxHeight: 'calc(100% - 140px)' }}
                  >
                      {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                          <MessageCircle className="w-12 h-12 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">
                            No messages yet. Start the conversation!
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((msg) => {
                            const isMine = msg.sender_id === user?.id;
                            return (
                              <div
                                key={msg.id}
                                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                    isMine
                                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white'
                                      : 'bg-accent text-foreground'
                                  }`}
                                >
                                  <p className="text-sm">{msg.content}</p>
                                  <div
                                    className={`flex items-center gap-1 text-xs mt-1 ${
                                      isMine ? 'text-white/70' : 'text-muted-foreground'
                                    }`}
                                  >
                                    <span>{formatTime(msg.created_at)}</span>
                                    {isMine && (
                                      <span className="ml-1">
                                        {msg.read ? (
                                          // Double tick green (read)
                                          <span className="inline-flex text-emerald-300">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z"/>
                                              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" transform="translate(3, 0)"/>
                                            </svg>
                                          </span>
                                        ) : msg.delivered ? (
                                          // Double tick gray (delivered)
                                          <span className="inline-flex text-white/50">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z"/>
                                              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" transform="translate(3, 0)"/>
                                            </svg>
                                          </span>
                                        ) : (
                                          // Single tick (sent)
                                          <span className="inline-flex text-white/50">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z"/>
                                            </svg>
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </div>

                  {/* Message Input - Always Visible at Bottom */}
                  <div className="p-4 border-t border-border flex-shrink-0 bg-background">
                    {messageError && (
                      <Alert className="mb-3 bg-red-500/10 border-red-500/50">
                        <AlertCircle className="w-4 h-4 text-red-400" />
                        <AlertDescription className="text-red-400">
                          {messageError}
                        </AlertDescription>
                      </Alert>
                    )}
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          if (messageError) setMessageError(null);
                        }}
                        className="flex-1"
                        disabled={sending}
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="bg-gradient-to-r from-emerald-500 to-teal-600"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </form>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                  <MessageCircle className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Select a Conversation
                  </h3>
                  <p className="text-muted-foreground max-w-sm">
                    Choose a conversation from the list or start a new one by
                    visiting a verified creator's profile and clicking "Message".
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* New Message Dialog - Full Screen Overlay */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent className="sm:max-w-[600px] max-w-[95vw] bg-background border shadow-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              Start New Conversation
            </DialogTitle>
            <DialogDescription>
              Search and select a verified creator to start messaging
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 overflow-hidden">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search verified creators..."
                value={creatorSearchQuery}
                onChange={(e) => setCreatorSearchQuery(e.target.value)}
                className="pl-9 focus:ring-1 focus:ring-emerald-500 focus:ring-offset-0"
              />
            </div>

            {/* Verified Creators List */}
            <ScrollArea className="h-[400px]">
              {loadingCreators ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : verifiedCreators.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <Users className="w-10 h-10 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm">
                    {creatorSearchQuery ? 'No creators found' : 'No verified creators available'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1 pr-2">
                  {verifiedCreators.map((creator) => (
                    <div
                      key={creator.id}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary transition-colors cursor-pointer"
                      onClick={() => {
                        setShowNewMessageModal(false);
                        startConversationWith(creator.id);
                      }}
                    >
                      <Avatar className="w-9 h-9 flex-shrink-0">
                        <AvatarImage src={creator.avatar} alt={creator.name} />
                        <AvatarFallback className="text-xs">{creator.name?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-foreground text-sm truncate">
                            {creator.name}
                          </span>
                          {creator.verified && (
                            <BadgeCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {(creator.bio || 'Verified Creator').substring(0, 40)}{creator.bio && creator.bio.length > 40 ? '...' : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white flex-shrink-0 h-7 text-xs px-2"
                      >
                        Message
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default MessagesPage;
